'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import JsonLd from '@src/components/Shared/JsonLd';
import ScrollToContainer from '@src/components/Shared/ScrollToContainer';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import ClientSuccess from '@src/components/Sections/ClientSuccess';
import NextImage from '@src/components/Shared/NextImage';
import RenderButtons from '@src/components/Shared/RenderButtons';

import HeroPrimary from '@src/components/Sections/HeroPrimary';
import ImageTextPrimary from '@src/components/Sections/ImageTextPrimary';
import CallToAction from '@src/components/Sections/CallToAction';
import { PathologiesClientPropTypes } from './Pathologies.types';
import usePreviewContent from '@src/hooks/usePreviewContent';
import constructURL from '@src/utils/constructURL';
import HealthGrid from '@src/components/Sections/HealthGrid';
import VideoTextPrimary from '@src/components/Sections/VideoTextPrimary';
import NextLink from '@src/components/Shared/NextLink';
import SocialEmbeds from '@src/components/Shared/SocialEmbeds';
import Testimonials from '@src/components/Sections/Testimonials';

const PathologiesClient: React.FC<PathologiesClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode, slug: '/pathologies', baseURL: '' })
  );
  setContentType('site-page');

  const pathologiesJsonLd = JsonLd({
    name: page?.pageName,
    pageUrl: `/${page?.languageCode?.toLowerCase()}${page?.slug === '/' ? '' : page?.slug}`,
    thumbnailUrl: page?.seoMetadata?.previewImage?.url,
    description: page?.seoMetadata?.description,
    datePublished: page?.sys?.firstPublishedAt,
    dateModified: page?.sys?.publishedAt,
    locale: page?.languageCode?.toLowerCase(),
  });

  return (
    <ScrollToContainer>
      {/* Add JSON-LD to your page */}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pathologiesJsonLd) }}
      />
      <div className='bg-primary'>
        <HeroPrimary
          attr='hero-component'
          id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
          className=''
          titleClassName='text-[4rem] md:text-[6rem] lg:text-[8rem] 2xl:text-[11.2412rem] 2xl:tracking-[-0.2432rem] leading-[4rem] md:leading-[6rem] lg:leading-[8rem] 2xl:leading-[11.2412rem] max-w-md md:max-w-[67rem] lg:max-w-[86rem] 2xl:max-w-[127.3rem] 2xl:locale-es:max-w-[114.3rem] 2xl:tracking-[-0.152rem]'
          bodyCopyClassName='text-[1.6rem] [&>p]:text-shadow-custom md:text-[1.8rem] lg:text-[2rem] 2xl:text-[2.4rem] leading-[2.4rem] md:leading-[2.7rem] lg:leading-[3rem] 2xl:leading-[3.6rem] max-w-md md:max-w-5xl lg:max-w-[53.78rem] 2xl:max-w-[78.4rem]'
          title={page?.hero?.items?.[0]?.title}
          bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
        />
      </div>

      <VideoTextPrimary
        reversed
        videoClassName='lg:w-[50rem] 2xl:w-[58rem] 3xl:w-[65rem] mb-[2.4rem] pl-[2.4rem] md:pl-0'
        id={page?.orthopaedics?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.orthopaedics?.items?.[0]?.title}
        videoURL={page?.orthopaedics?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.orthopaedics?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <HealthGrid
        data={page?.convalesce?.items?.[0]?.topicCollection?.items}
        id={page?.convalesce?.items?.[0]?.scrollTo?.slice(1)}
        attr='customcolumns-component'
      />

      <Container>
        <SectionHeader
          className='mx-auto mb-[2.4rem] md:mb-[4.1rem] 2xl:mb-[7.2rem]'
          titleClassName='text-[2.2rem] leading-[2.6rem] md:text-[2.8rem] leading-[3.0rem] md:max-w-[44rem] md:max-w-[76.5rem] xl:text-[4.8rem] xl:leading-[5.2rem]'
          title={page?.valdTechnology?.items?.[0]?.title}
          align='center'
        />

        <SocialEmbeds
          className='mx-auto max-w-[103.4rem] overflow-hidden rounded-[0.6rem] shadow-[0px_5px_15px_rgba(0,0,0,0.35)]'
          data={page?.valdTechnology?.items?.[0]?.extraAssetsCollection?.items?.[0]}
        />
      </Container>

      <ImageTextPrimary
        attr='itp-musculoskeletal-conditions'
        id={page?.mskConditions?.items?.[0]?.scrollTo?.slice(1)}
        imgURL={page?.mskConditions?.items?.[0]?.mediaPrimary?.url}
        pretitle={page?.mskConditions?.items?.[0]?.preTitle}
        title={page?.mskConditions?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.mskConditions?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.mskConditions?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.phoneMockup?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='mb-[0] md:mb-[4rem]! lg:mb-[8rem]! lg:pt-[6.4rem]'
      >
        <NextLink href={page?.phoneMockup?.items?.[0]?.extraAssetsCollection?.items?.[0]?.url}>
          <NextImage
            className='relative mx-auto hidden w-full overflow-visible! lg:mt-[-5rem] lg:block'
            src={page?.phoneMockup?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.url}
            alt={
              page?.phoneMockup?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.description
            }
            objectFit='cover'
          />
        </NextLink>

        <NextLink href={page?.phoneMockup?.items?.[0]?.extraAssetsCollection?.items?.[1]?.url}>
          <NextImage
            className='relative mx-auto h-[40rem] w-full max-w-[33rem] lg:hidden'
            src={page?.phoneMockup?.items?.[0]?.extraAssetsCollection?.items?.[1]?.media?.url}
            alt={
              page?.phoneMockup?.items?.[0]?.extraAssetsCollection?.items?.[1]?.media?.description
            }
            objectFit='cover'
          />
        </NextLink>
      </Container>

      <ImageTextPrimary
        attr='itp-ageing-and-balance'
        reversed
        id={page?.care?.items?.[0]?.scrollTo?.slice(1)}
        imgURL={page?.care?.items?.[0]?.mediaPrimary?.url}
        imgURLSecondary={page?.care?.items?.[0]?.mediaSecondary?.url}
        imgURLSecondaryClassName='absolute! top-[10%] z-1 right-[calc(0%+2.4rem)] w-[50%] md:top-[28%] md:w-[48%] md:right-[calc(0%+6.3rem)] md:right-[calc(0%+6.4rem)] lg:w-[40%] lg:top-[78%] lg:transform lg:translate-y-[-50%] xl:w-[30%] xl:right-[calc(0%+12.8rem)] 3xl:right-[calc(0%+4rem)] 4xl:right-[0]'
        pretitle={page?.care?.items?.[0]?.preTitle}
        title={page?.care?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.care?.items?.[0]?.bodyCopy?.json?.content}
              className='[&>p>a]:drop-shadow-xs'
            />
            <RenderButtons items={page?.care?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextPrimary
        attr='itp-benchmark-your-workforce'
        id={page?.benchmark?.items?.[0]?.scrollTo?.slice(1)}
        imgURL={page?.benchmark?.items?.[0]?.mediaPrimary?.url}
        pretitle={page?.benchmark?.items?.[0]?.preTitle}
        title={page?.benchmark?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.benchmark?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <NextImage
        id={page?.weExist?.items?.[0]?.scrollTo?.slice(1)}
        src={page?.weExist?.items?.[0]?.mediaPrimary?.url}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] 2xl:mb-[5.6rem] [&>img]:object-top'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            className='h-full'
            preTitleClassName='mb-[1.6rem]'
            title={page?.weExist?.items?.[0]?.title}
            titleClassName='text-left md:text-center text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] lg:text-[4.8rem] 2xl:text-[7.2rem] lg:leading-[5rem] 2xl:leading-[7.56rem]'
            childrenClassName='relative max-w-[88.8rem]!'
            titleHighlightBold
            align='center'
          >
            <RenderBodyCopy
              className='text-body-copy--light mx-auto mt-[2.4rem] max-w-full! text-left text-[1.6rem] font-extralight md:text-center md:text-[2.4rem] 2xl:mt-[3.2rem] 2xl:max-w-[88.8rem]'
              bodyCopy={page?.weExist?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-left md:justify-center'
              items={page?.weExist?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </Container>
      </NextImage>

      <ClientSuccess data={page?.clientSuccess?.items?.[0]} />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={data.page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </ScrollToContainer>
  );
};

export default PathologiesClient;
